import { useEffect, useState } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut, MapPin, MapPinOff, Loader2, ArrowLeftRight } from 'lucide-react';

import { useAuthStore } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';
import { getOneFix, postOneFix, startWatching, stopWatching } from '@/lib/driverLocation';
import { cn } from '@/lib/cn';
import type { DriverMe } from '@/types/driver';

/**
 * /driver layout. No sidebar, no dense admin chrome — a thin top bar and
 * full-width content. This is used one-handed, often outdoors or in a
 * stairwell with bad light and a bag under one arm, so every screen inside
 * gets to be as plain and large-tapped as it wants without fighting a
 * shell built for a desk.
 *
 * Also owns the online/offline toggle and the location watch (Phase 17
 * Commit 2) — mounted here, not on any one screen, so tracking keeps
 * running across every driver page and resumes automatically on refresh
 * if the driver was already on shift.
 */
export function DriverShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clear = useAuthStore((s) => s.clear);
  const user = useAuthStore((s) => s.user);

  const meQuery = useQuery({
    queryKey: ['driver', 'me'],
    queryFn: () => api<DriverMe>('/rider/me'),
  });
  const isOnShift = meQuery.data?.rider.is_on_shift ?? false;

  const [locationError, setLocationError] = useState<string | null>(null);
  const [gettingFix, setGettingFix] = useState(false);

  // Resume tracking on a page refresh if the driver was already on shift —
  // otherwise a reload would silently stop posting location until they
  // toggle off and back on. startWatching() is idempotent, and the watch
  // is a module-level singleton, not tied to this component's lifecycle --
  // stopping it only ever happens explicitly, from goOffline/signOut below.
  useEffect(() => {
    if (isOnShift) startWatching();
  }, [isOnShift]);

  const goOnline = useMutation({
    mutationFn: async () => {
      setGettingFix(true);
      let fix;
      try {
        fix = await getOneFix();
      } finally {
        setGettingFix(false);
      }
      await postOneFix(fix);
      return api('/rider/me/availability', { method: 'PATCH', body: { is_on_shift: true } });
    },
    onSuccess: async () => {
      setLocationError(null);
      startWatching();
      await queryClient.invalidateQueries({ queryKey: ['driver', 'me'] });
    },
    onError: (err: unknown) => {
      // Covers both failure shapes: a geolocation permission/timeout error
      // (plain Error, from getOneFix) and the backend's 422 when the posted
      // fix isn't fresh enough (ApiError, details.code LOCATION_REQUIRED) --
      // both already carry a clear, user-facing message.
      const message = err instanceof ApiError || err instanceof Error
        ? err.message
        : 'Could not get your location. Enable location access and try again.';
      setLocationError(message);
    },
  });

  const goOffline = useMutation({
    mutationFn: () => api('/rider/me/availability', { method: 'PATCH', body: { is_on_shift: false } }),
    onSuccess: async () => {
      stopWatching();
      await queryClient.invalidateQueries({ queryKey: ['driver', 'me'] });
    },
  });

  function signOut() {
    stopWatching();
    clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-bone flex flex-col">
      <header className="sticky top-0 z-20 bg-ink text-bone px-4 h-14 flex items-center justify-between shadow-sm">
        <span className="font-display text-lg" style={{ fontVariationSettings: "'opsz' 90, 'wght' 500" }}>
          {user?.name || 'Driver'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={goOnline.isPending || goOffline.isPending || meQuery.isLoading}
            onClick={() => (isOnShift ? goOffline.mutate() : goOnline.mutate())}
            className={cn(
              'flex items-center gap-1.5 h-9 px-3 rounded-full font-body text-xs transition-colors disabled:opacity-50',
              isOnShift ? 'bg-success/20 text-success' : 'bg-bone/10 text-bone/70',
            )}
          >
            {goOnline.isPending || gettingFix
              ? <Loader2 size={13} className="animate-spin" />
              : isOnShift ? <MapPin size={13} /> : <MapPinOff size={13} />}
            {gettingFix ? 'Locating…' : isOnShift ? 'Online' : 'Offline'}
          </button>
          <Link to="/driver/handovers" aria-label="Handovers"
                className="w-11 h-11 flex items-center justify-center text-bone/70 hover:text-bone active:scale-95 transition-transform">
            <ArrowLeftRight size={18} />
          </Link>
          <button type="button" onClick={signOut} aria-label="Sign out"
                  className="w-11 h-11 -me-2 flex items-center justify-center text-bone/70 hover:text-bone active:scale-95 transition-transform">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {locationError && (
        <div className="px-4 py-2.5 bg-danger/10 border-b border-danger/20">
          <p className="font-body text-xs text-danger max-w-lg mx-auto">{locationError}</p>
        </div>
      )}

      <main className="flex-1 px-4 py-4 max-w-lg mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}
